import axios from 'axios'

// Create axios instance with base configuration
// Use VITE_API_BASE_URL for Vite projects
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'

const api = axios.create({
  baseURL: `${apiBaseUrl}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for session-based auth
  timeout: 5000, // 5 second timeout to prevent hanging requests
})

// Add request interceptor for auth tokens (if using token-based auth)
// Note: MVP uses session-based auth (cookies), so this may not be needed
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('authToken')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// API Service functions
export const apiService = {
  // Authentication
  auth: {
    login: (credentials: { email: string; password: string }) =>
      api.post('/auth/login', credentials),
    logout: () => api.post('/auth/logout'),
    me: () => api.get('/auth/me'),
  },

  // Product Insights
  productInsights: {
    getInsights: () => api.get('/product-insights'),
    syncData: (config: any) => api.post('/product-insights/sync', config),
    exportData: () => api.get('/product-insights/export'),
  },

  // Customer Insights
  customerInsights: {
    getInsights: () => api.get('/customer-insights'),
    getSegments: () => api.get('/customer-insights/segments'),
    syncData: (config: any) => api.post('/customer-insights/sync', config),
    exportReport: () => api.get('/customer-insights/export'),

    // Synthesis controls
    // These operations can take a long time, so use extended timeout (60s)
    synthesis: {
      run: (payload: { user_id?: string; processed_doc_ids?: string[]; account_ids?: string[]; limit?: number }) =>
        api.post('/customer-insights/synthesis/run', payload, { timeout: 60000 }),
      runForAccounts: (payload: { user_id?: string; account_ids: string[]; limit?: number }) =>
        api.post('/customer-insights/synthesis/run-for-accounts', payload, { timeout: 60000 }),
      runSince: (payload: { user_id?: string; since_ts: string; limit?: number }) =>
        api.post('/customer-insights/synthesis/run-since', payload, { timeout: 60000 }),
      rebuild: (payload: { user_id?: string; batch_size?: number }) =>
        api.post('/customer-insights/synthesis/rebuild', payload, { timeout: 60000 }),
    },
  },

  // Settings
  settings: {
    getIntegrations: () => api.get('/settings/integrations'),
    saveIntegrations: (config: any) => api.post('/settings/integrations', config),
    getPipelineConfig: () => api.get('/settings/pipeline'),
    savePipelineConfig: (config: any) => api.post('/settings/pipeline', config),
  },

  // OAuth
  oauth: {
    /**
     * Authorize Google OAuth
     * Redirects to backend OAuth endpoint which will redirect to Google's consent page.
     * 
     * Local dev: Uses relative path '/api/oauth/connect/google' which works with Vite proxy
     * Staging/Prod: Uses full backend URL from VITE_API_BASE_URL
     * 
     * The backend route is /api/oauth/connect/{provider}?user_id=xxx
     */
    authorizeGoogle: (userId?: string) => {
      if (!userId) {
        console.error('User ID is required for OAuth authorization')
        return
      }
      // In local dev (no VITE_API_BASE_URL), use relative path with Vite proxy
      // In staging/prod (VITE_API_BASE_URL set), use full backend URL
      const backendUrl = import.meta.env.VITE_API_BASE_URL
      if (backendUrl) {
        // Staging/production: use full backend URL
        window.location.href = `${backendUrl}/api/oauth/connect/google?user_id=${userId}`
      } else {
        // Local dev: use relative path (Vite proxy will handle it)
        window.location.href = `/api/oauth/connect/google?user_id=${userId}`
      }
    },
    authorizeSalesforce: (userId?: string) => {
      if (!userId) {
        console.error('User ID is required for OAuth authorization')
        return
      }
      const backendUrl = import.meta.env.VITE_API_BASE_URL
      if (backendUrl) {
        window.location.href = `${backendUrl}/api/oauth/connect/salesforce?user_id=${userId}`
      } else {
        window.location.href = `/api/oauth/connect/salesforce?user_id=${userId}`
      }
    },
  },

  // Alerts
  alerts: {
    list: (params?: {
      account_id?: string
      match_type?: string
      status?: string
      min_priority?: number
      limit?: number
      sort_by?: 'priority' | 'created_at'
    }) => api.get('/alerts', { params }),
    get: (alertId: string) => api.get(`/alerts/${alertId}`),
    action: (alertId: string, data: { action: string; notes?: string }) =>
      api.post(`/alerts/${alertId}/action`, data),
    dismiss: (alertId: string, data: { reason: string; notes?: string }) =>
      api.post(`/alerts/${alertId}/dismiss`, data),
    updateStatus: (alertId: string, status: string) =>
      api.patch(`/alerts/${alertId}/status`, { status }),
  },

  // Alert Review (Reviewer-only)
  alertReview: {
    list: (params?: {
      user_id?: string
      account_id?: string
      match_type?: string
      min_priority?: number
      limit?: number
      sort_by?: 'priority' | 'created_at'
    }) => api.get('/alerts/review', { params }),
    update: (alertId: string, data: {
      alert_priority?: number
      action_recommendation?: string
      alert_reason?: string
      suggested_timing?: string
    }) => api.patch(`/alerts/${alertId}/update`, data),
    review: (alertId: string, data: {
      action: 'approve' | 'reject'
      annotation?: string
      reviewer_notes?: string
    }) => api.post(`/alerts/${alertId}/review`, data),
    batchReview: (data: {
      alert_ids: string[]
      action: 'approve' | 'reject'
      annotation?: string
      send_notification?: boolean
    }) => api.post('/alerts/review/batch', data),
    getStats: () => api.get('/alerts/review/stats'),
    getSourceData: (alertId: string) => api.get(`/alerts/${alertId}/source-data`),
  },

  // Google Drive
  googleDrive: {
    // Browse folders for customer insights
    browseCustomerFolders: (parentFolderId?: string, includeSamples: boolean = true) =>
      api.get('/customer-insights/google-drive/browse', {
        params: { parent_folder_id: parentFolderId, include_samples: includeSamples },
        timeout: 15000  // 15 second timeout for browse operations (can be slow with sample files)
      }),
    // Browse folders for product insights
    browseProductFolders: (parentFolderId?: string, includeSamples: boolean = true) =>
      api.get('/product-insights/google-drive/browse', {
        params: { parent_folder_id: parentFolderId, include_samples: includeSamples },
        timeout: 15000  // 15 second timeout for browse operations (can be slow with sample files)
      }),
    // Save folder configuration (both customer and product insights)
    saveConfig: (config: {
      customer_insights_folder_id?: string
      customer_insights_folder_name?: string
      product_insights_folder_id?: string
      product_insights_folder_name?: string
    }) => api.post('/customer-insights/google-drive/config', { config }),
    // Get current folder configuration
    getConfig: () => api.get('/customer-insights/google-drive/config'),
    // Disconnect Google Drive (uses session-based auth, no user_id needed)
    disconnect: () => api.post('/oauth/disconnect/google'),
  },

  // Pipeline Sync
  sync: {
    // Trigger unified pipeline (supports multiple sources and domains)
    // Uses longer timeout (30s) as pipeline initialization can take time
    trigger: (config?: {
      sources?: string[]
      domains?: string[]
      force?: boolean
      async?: boolean
    }) => api.post('/sync', config || {}, {
      timeout: 30000  // 30 second timeout for pipeline operations
    }),
    // Get sync status
    getStatus: () => api.get('/sync/status'),
  },

  // Users
  users: {
    // Get user profile
    getProfile: () => api.get('/users/profile'),
    // Update user profile (onboarding fields)
    updateProfile: (data: {
      company_name?: string
      role?: string
      team?: string
      product_value?: string
      industry?: string
      business_focus?: string
      customer_segments?: string[]
      customer_locations?: string[]
    }) => api.put('/users/profile', data),
    // Change password
    changePassword: (data: {
      current_password: string
      new_password: string
    }) => api.post('/users/change-password', data),
  },
}

export default api
